import type { LayerSettings } from './LayerSettingsPanel';
import { ChoroplethColorScale } from './ChoroplethColorScale';
import { CategoricalLegendPill } from './CategoricalLegendPill';
import { FLOOD_EXPOSURE_GRID_LEGEND } from '../../lib/floodExposure';
import { FORECAST_WEATHER_LAYER_DEFS, type ForecastWeatherLayerKey } from '../../lib/forecastWeather';
import { OBSERVED_WEATHER_UI_DEFS } from '../../lib/observedWeather';
import type { ForecastVariable, ForecastDaySummary } from '../weather/WeatherDashboard';

type MapDataLegendLayers = Pick<
  LayerSettings,
  | 'observedWeatherTemp'
  | 'observedWeatherWind'
  | 'observedWeatherPrecip'
  | 'forecastWeatherTemp'
  | 'forecastWeatherPrecip'
  | 'forecastWeatherWind'
  | 'forecastWeatherTempPeriods'
  | 'forecastWeatherPrecipPeriods'
  | 'forecastWeatherWindPeriods'
  | 'observedWeatherTempPeriods'
  | 'observedWeatherWindPeriods'
  | 'observedWeatherPrecipPeriods'
  | 'floodExposure'
  | 'floodExposureYears'
>;

const FORECAST_VAR_TO_KEY: Record<ForecastVariable, ForecastWeatherLayerKey> = {
  temp: 'forecastWeatherTemp',
  precip: 'forecastWeatherPrecip',
  wind: 'forecastWeatherWind',
};

function layerHasActivePeriods(
  layers: MapDataLegendLayers,
  settingsKey: ForecastWeatherLayerKey,
  periodKey: keyof MapDataLegendLayers,
): boolean {
  if (!layers[settingsKey]) return false;
  const periods = layers[periodKey];
  return Array.isArray(periods) && periods.length > 0;
}

function observedHasPeriods(layers: MapDataLegendLayers, periodKey: keyof MapDataLegendLayers): boolean {
  const periods = layers[periodKey];
  return Array.isArray(periods) && periods.length > 0;
}

function userMarkerValue(
  variable: ForecastVariable | null,
  days: ForecastDaySummary[],
): number | null {
  if (!variable || days.length === 0) return null;
  const day = days[0];
  if (variable === 'temp') return day.temperature_c ?? null;
  if (variable === 'precip') return day.precipitation_mm ?? null;
  return day.wind_speed_ms ?? null;
}

interface MapDataLegendsProps {
  layers: MapDataLegendLayers;
  /** When set, only show the matching forecast legend (weather view). */
  activeForecastVariable?: ForecastVariable | null;
  forecastDays?: ForecastDaySummary[];
}

/** Choropleth / categorical legends for active map layers. */
export function MapDataLegends({
  layers,
  activeForecastVariable = null,
  forecastDays = [],
}: MapDataLegendsProps) {
  const markerValue = userMarkerValue(activeForecastVariable, forecastDays);

  const observedActive = OBSERVED_WEATHER_UI_DEFS.filter((def) => {
    if (!layers[def.settingsKey as keyof MapDataLegendLayers]) return false;
    return observedHasPeriods(layers, def.periodSettingsKey as keyof MapDataLegendLayers);
  });

  const forecastActive = FORECAST_WEATHER_LAYER_DEFS.filter((def) => {
    if (activeForecastVariable) {
      if (def.settingsKey !== FORECAST_VAR_TO_KEY[activeForecastVariable]) return false;
    } else if (!layers[def.settingsKey as keyof MapDataLegendLayers]) {
      return false;
    }
    return layerHasActivePeriods(
      layers,
      def.settingsKey,
      def.periodSettingsKey as keyof MapDataLegendLayers,
    );
  });

  const floodActive = layers.floodExposure || layers.floodExposureYears.length > 0;

  if (observedActive.length === 0 && forecastActive.length === 0 && !floodActive) return null;

  return (
    <div className="flex w-full flex-col items-end gap-1.5" aria-live="polite">
      {floodActive && (
        <CategoricalLegendPill
          title="Flood exposure"
          items={FLOOD_EXPOSURE_GRID_LEGEND.map(({ label, color }) => ({ label, color }))}
          variant="map"
        />
      )}
      {forecastActive.map((def) => (
        <ChoroplethColorScale
          key={def.settingsKey}
          variant="map"
          markerValue={activeForecastVariable ? markerValue : null}
          infoText={def.info}
          mapBadge={activeForecastVariable ? 'Today' : undefined}
          mapBadgeClassName="text-emerald-700 dark:text-emerald-300"
          {...def.legend}
        />
      ))}
      {observedActive.map((def) => (
        <ChoroplethColorScale
          key={def.settingsKey}
          variant="map"
          infoText={def.info}
          mapBadge="Past years"
          mapBadgeClassName="text-amber-800 dark:text-amber-300"
          {...def.legend}
        />
      ))}
    </div>
  );
}
